<template lang="html">
  <section
    v-if="roots.length"
    class="home-root-tiles mx-auto w-full max-w-(--ui-container) px-3 py-6 sm:px-6 sm:py-8"
    aria-labelledby="home-root-tiles-heading"
  >
    <header class="mb-4 sm:mb-5">
      <h2
        id="home-root-tiles-heading"
        class="text-2xl font-bold tracking-tight text-highlighted sm:text-3xl"
      >
        Каталог
      </h2>
    </header>

    <div class="home-root-tiles__carousel">
      <SwiperContainer
        class="home-root-tiles__slider"
        :modules="modules"
        :space-between="density.spaceBetween"
        :slides-per-view="density.slidesPerView"
        :breakpoints="density.breakpoints"
        :pagination="pagination"
        watch-overflow
      >
        <SwiperSlide
          v-for="(category, index) in roots"
          :key="category.id"
          class="home-root-tiles__slide !h-auto"
        >
          <CategoryCard
            size="root"
            :category="category"
            :eager="index < eagerCount"
          />
        </SwiperSlide>
      </SwiperContainer>

      <!-- mt-4 + h-6: clear gap under slides; Swiper fills bullets into #paginationDomId -->
      <div class="mt-4 flex h-6 w-full items-center justify-center">
        <div
          :id="paginationDomId"
          class="home-root-tiles__pagination swiper-pagination"
          role="navigation"
          aria-label="Категории"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
/**
 * Home root category tiles (Phase 8 / S29 / R10).
 * All roots (by order) in a horizontal Swiper via CategoryCard. Hidden when empty.
 * SSR/CLS notes: home-catalog-entry/notes.md (Gotchas).
 */
import { Pagination } from 'swiper/modules'
import type { SwiperOptions } from 'swiper/types'
import { Swiper as SwiperContainer, SwiperSlide } from 'swiper/vue'
import 'swiper/swiper-bundle.css'

import CategoryCard from '#Products/components/Catalog/CategoryCard.vue'
import {
  HOME_ROOT_TILES_EAGER_COUNT,
  homeRootTilesPreInitCss,
  homeRootTilesSwiperDensity,
} from '#Products/support/homeRootTilesLayout'

const { allRoot } = await useCategories()

const modules = [Pagination]
const density = homeRootTilesSwiperDensity()
const eagerCount = HOME_ROOT_TILES_EAGER_COUNT

/** Per-instance id — avoids document-wide `.class` querySelector collisions. */
const paginationDomId = useId()

const pagination: SwiperOptions['pagination'] = {
  el: `[id="${paginationDomId}"]`,
  clickable: true,
  // external in-flow el — dynamicBullets applies absolute + translateX/width via JS
  type: 'bullets',
}

const roots = computed(() =>
  [...(allRoot.value || [])].toSorted((a, b) => a.order - b.order),
)

useHead({
  style: [
    {
      key: 'home-root-tiles-preinit',
      innerHTML: homeRootTilesPreInitCss(),
    },
  ],
})
</script>

<style lang="css">
.home-root-tiles__slider {
  --swiper-theme-color: var(--color-tertiary-500);
}

/* External el stays in the mt-4/h-6 row — cancel Swiper absolute defaults. */
.home-root-tiles__carousel .home-root-tiles__pagination {
  position: static;
  inset: auto;
  display: flex;
  justify-content: center;
  width: 100%;
  transform: none;
}
</style>
